import type { CSSProperties, ReactNode } from "react";
import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { Form, Input, Button, Typography } from "antd";

import type { SettingsResponse } from "@chirpstack/chirpstack-api-grpc-web/api/internal_pb";
import { OpenIdConnectLoginRequest, OAuth2LoginRequest } from "@chirpstack/chirpstack-api-grpc-web/api/internal_pb";

import SessionStore from "../../stores/SessionStore";
import InternalStore from "../../stores/InternalStore";
import { useTitle } from "../helpers";
import { IOX_BRANDING } from "../../branding/ioxBranding";

// Login page — single fixed layout (the old card-vs-split per-tenant
// switch was removed with Slice 5). Left ~30% white panel with the
// EAMS Technologies logo top-left and the form below; right ~70%
// shows the branding artwork. Matches the design mock in the Slice 5
// brief. No tenant-specific overrides, no OIDC/OAuth2 hot-branding —
// this is deliberately one layout for one product.

function bundledAssetAbsoluteUrl(url: string): string {
  // Hash-routed apps (#/login) need absolute URLs for background-image
  // so relative paths don't resolve against the hash fragment.
  if (
    url.startsWith("http://") ||
    url.startsWith("https://") ||
    url.startsWith("data:") ||
    url.startsWith("blob:")
  ) {
    return url;
  }
  if (typeof window === "undefined") {
    return url;
  }
  const path = url.startsWith("/") ? url : `/${url}`;
  return `${window.location.origin}${path}`;
}

interface LoginFormValues {
  email: string;
  password: string;
}

function LoginForm() {
  const navigate = useNavigate();

  const onFinish = (values: LoginFormValues) => {
    SessionStore.login(values.email, values.password, () => {
      navigate("/");
    });
  };

  return (
    <Form layout="vertical" onFinish={onFinish} requiredMark="optional">
      <Form.Item
        label="Email address"
        name="email"
        rules={[{ required: true, message: "Please enter your email!" }]}
      >
        <Input autoComplete="username" />
      </Form.Item>
      <Form.Item
        label="Password"
        name="password"
        rules={[{ required: true, message: "Please enter your password!" }]}
      >
        <Input.Password autoComplete="current-password" />
      </Form.Item>
      <Form.Item>
        <Button type="primary" htmlType="submit" block>
          Login
        </Button>
      </Form.Item>
    </Form>
  );
}

function FederatedLogin({ loginUrl, loginLabel }: { loginUrl: string; loginLabel: string }) {
  return (
    <a href={loginUrl}>
      <Button type="primary" block>
        {loginLabel}
      </Button>
    </a>
  );
}

function LoginShell({ children }: { children: ReactNode }) {
  const bg = bundledAssetAbsoluteUrl(IOX_BRANDING.loginBackgroundImageUrl);
  const logoSrc = bundledAssetAbsoluteUrl(IOX_BRANDING.loginLogoImageUrl);

  const shell: CSSProperties = {
    position: "fixed",
    inset: 0,
    zIndex: 0,
    width: "100%",
    minHeight: "100vh",
    display: "flex",
    flexDirection: "row",
    alignItems: "stretch",
  };

  // Left form panel. White background, logo pinned top-left, form
  // centred vertically below it. The fixed max-width keeps the form
  // from getting awkwardly wide on very large screens.
  const leftPanel: CSSProperties = {
    position: "relative",
    zIndex: 1,
    flex: "0 0 32%",
    minWidth: 340,
    maxWidth: 460,
    background: "#ffffff",
    padding: "40px 48px",
    display: "flex",
    flexDirection: "column",
    boxShadow: "4px 0 24px rgba(15, 23, 42, 0.06)",
  };

  // Right artwork panel. White base under the PNG so alpha in the
  // branding image never reveals the underlying app shell.
  const rightPanel: CSSProperties = {
    position: "relative",
    zIndex: 1,
    flex: 1,
    minWidth: 0,
    minHeight: "100vh",
    backgroundColor: "#ffffff",
    backgroundImage: `url(${bg})`,
    backgroundSize: "cover",
    backgroundPosition: "center center",
    backgroundRepeat: "no-repeat",
  };

  return (
    <div style={shell}>
      <div style={leftPanel}>
        {/* Logo is part of the vertically-centered stack (not pinned
            top-left of the panel) — the form block sits a touch above
            middle with the logo directly above the heading. */}
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "stretch",
          }}
        >
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 32 }}>
            <img
              src={logoSrc}
              alt={IOX_BRANDING.productName}
              style={{ display: "block", maxWidth: 320, width: "100%", height: "auto" }}
            />
          </div>
          {children}
        </div>
      </div>
      <div style={rightPanel} aria-hidden />
    </div>
  );
}

function Login() {
  useTitle("Login");
  const location = useLocation();
  const navigate = useNavigate();

  const [loaded, setLoaded] = useState<boolean>(false);
  const [oidcEnabled, setOidcEnabled] = useState<boolean>(false);
  const [oAuth2Enabled, setOAuth2Enabled] = useState<boolean>(false);
  const [oidcLoginLabel, setOidcLoginLabel] = useState<string>("");
  const [oidcLoginUrl, setOidcLoginUrl] = useState<string>("");
  const [oAuth2LoginLabel, setOAuth2LoginLabel] = useState<string>("");
  const [oAuth2LoginUrl, setOAuth2LoginUrl] = useState<string>("");

  useEffect(() => {
    SessionStore.logout(true, () => {});

    InternalStore.settings((resp: SettingsResponse) => {
      const oidc = resp.getOpenidConnect()!;
      const oAuth2 = resp.getOauth2()!;

      setOidcEnabled(oidc.getEnabled());
      setOidcLoginLabel(oidc.getLoginLabel());
      setOidcLoginUrl(oidc.getLoginUrl());

      setOAuth2Enabled(oAuth2.getEnabled());
      setOAuth2LoginLabel(oAuth2.getLoginLabel());
      setOAuth2LoginUrl(oAuth2.getLoginUrl());

      const query = new URLSearchParams(location.search);
      const callbackCode = query.get("code");
      const callbackState = query.get("state");

      if (callbackCode !== null || callbackState !== null) {
        // Callback from OIDC or OAuth2 provider.
        if (oidc.getEnabled()) {
          const req = new OpenIdConnectLoginRequest();
          req.setCode(callbackCode || "");
          req.setState(callbackState || "");

          SessionStore.openIdConnectLogin(req, () => {
            navigate("/");
          });
        } else if (oAuth2.getEnabled()) {
          const req = new OAuth2LoginRequest();
          req.setCode(callbackCode || "");
          req.setState(callbackState || "");

          SessionStore.oAuth2Login(req, () => {
            navigate("/");
          });
        }
      } else {
        if (oidc.getEnabled() && oidc.getLoginRedirect()) {
          window.location.replace(oidc.getLoginUrl());
        } else if (oAuth2.getEnabled() && oAuth2.getLoginRedirect()) {
          window.location.replace(oAuth2.getLoginUrl());
        } else {
          setLoaded(true);
        }
      }
    });
  }, [location, navigate]);

  if (!loaded) {
    return null;
  }

  const heading = (
    <Typography.Title level={3} style={{ marginTop: 0, marginBottom: 24 }}>
      Sign in
    </Typography.Title>
  );

  if (oidcEnabled) {
    return (
      <LoginShell>
        {heading}
        <FederatedLogin loginUrl={oidcLoginUrl} loginLabel={oidcLoginLabel} />
      </LoginShell>
    );
  } else if (oAuth2Enabled) {
    return (
      <LoginShell>
        {heading}
        <FederatedLogin loginUrl={oAuth2LoginUrl} loginLabel={oAuth2LoginLabel} />
      </LoginShell>
    );
  } else {
    return (
      <LoginShell>
        {heading}
        <LoginForm />
      </LoginShell>
    );
  }
}

export default Login;
